/**
 * `useThreads` — fetches the threads index and exposes a refresh
 * function. Plain `fetch` against `/api/ai/threads`, credentials
 * included so the auth cookie flows through.
 *
 * The hook stays minimal on purpose — the orchestrator updates the
 * locally-known title when `data-title` arrives over the chat stream,
 * so there's no need for a websocket-style live feed here.
 */
import { useCallback, useEffect, useState } from "react";
import { WEB_ENV } from "../../../lib/env.ts";
import type { ThreadSummary } from "../types.ts";

export interface UseThreadsState {
  threads: ThreadSummary[];
  setThreads: React.Dispatch<React.SetStateAction<ThreadSummary[]>>;
  refresh: () => Promise<void>;
}

export function useThreads(): UseThreadsState {
  const [threads, setThreads] = useState<ThreadSummary[]>([]);

  const refresh = useCallback(async () => {
    const response = await fetch(`${WEB_ENV.VITE_FLINT_API_URL}/api/ai/threads`, {
      credentials: "include",
    });
    if (!response.ok) return;
    const data = (await response.json()) as { threads: ThreadSummary[] };
    setThreads(data.threads);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { threads, setThreads, refresh };
}
